import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  Box,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Select,
  MenuItem,
  useTheme,
  FormControl,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { addCampaignRequest, updateCampaignRequest, getOutboundCdrRequest } from "api/api";

const validationSchema = Yup.object({
  campaign_name: Yup.string().required("Campaign name is required"),
  trunk_id: Yup.string().required("Trunk is required"),
  // hold_time: Yup.number().required("Hold Time is required"),
  routes: Yup.array()
    .of(
      Yup.object().shape({
        input_value: Yup.string().required("Input Value is required"),
        destination: Yup.string().required("Destination is required"),
      })
    )
    .min(1, "At least one route is required"),

  // audio_file2: Yup.mixed().nullable(),
  // audio_file3: Yup.mixed().nullable(),
});

function AddCampaignForm({ onSuccess, initialData = null, clickedBtn, setSubmitting }) {
  const [trunkOption, settrunkOption] = React.useState([]);
  const theme = useTheme();
  const isEditMode = Boolean(initialData);
  React.useEffect(() => {
    const fetchtrunkOption = async () => {
      try {
        const res = await getOutboundCdrRequest();
        settrunkOption(res.data.data); // Make sure your API returns `data.data`
      } catch (error) {
        console.error("Error fetching trunk_id options:", error);
      }
    };

    fetchtrunkOption();
  }, []);

  const formik = useFormik({
    initialValues: {
      trunk_id: initialData?.trunk?.id ? initialData.trunk.id.toString() : "",
      campaign_name: initialData?.campaign_name || "",
      description: initialData?.description || "",
      caller_id: initialData?.caller_id || "",
      // hold_time: initialData?.hold_time || 5,
      audio_file: "",
      // audio_file2: null,
      // audio_file3: "",
      routes:
        initialData?.dtmfs?.length > 0
          ? initialData.dtmfs.map((opt) => ({
              input_value: opt.input_value?.toString() || "",
              destination: opt.destination || "",
            }))
          : [{ input_value: "", destination: "" }],
    },

    enableReinitialize: true,
    validationSchema,
    context: { isEditMode },
    onSubmit: async (values, { resetForm }) => {
      try {
        let response;

        const formData = new FormData();
        formData.append("trunk_id", values.trunk_id);
        formData.append("campaign_name", values.campaign_name);
        formData.append("description", values.description || "");
        formData.append("caller_id", values.caller_id || "");
        // formData.append("hold_time", values.hold_time || "");

        if (values.audio_file) formData.append("audio_file", values.audio_file);
        // if (values.audio_file2) formData.append("audio_file2", values.audio_file2);
        // if (values.audio_file3) formData.append("audio_file3", values.audio_file3);

        values.routes.forEach((route, index) => {
          formData.append(`dtmf[${index}][input_value]`, route.input_value);
          formData.append(`dtmf[${index}][destination]`, route.destination);
        });

        response = isEditMode
          ? await updateCampaignRequest(formData, initialData.id)
          : await addCampaignRequest(formData);

        resetForm();
        setSubmitting(false);
        onSuccess && onSuccess();
      } catch (error) {
        setSubmitting(false);

        if (error?.response?.data?.errors) {
          const apiErrors = {};

          Object.keys(error.response.data.errors).forEach((key) => {
            apiErrors[key] = Array.isArray(error.response.data.errors[key])
              ? error.response.data.errors[key][0]
              : error.response.data.errors[key];
          });

          formik.setErrors(apiErrors);
        }

        console.log("API Error =>", error?.response?.data);
      }
    },
  });

  const handleAddRoute = () => {
    formik.setFieldValue("routes", [...formik.values.routes, { input_value: "", destination: "" }]);
  };

  const handleRemoveRoute = (index) => {
    const updated = formik.values.routes.filter((_, i) => i !== index);
    formik.setFieldValue("routes", updated);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${theme.palette.background.border}`,
          padding: "10px 20px",
        }}
      >
        <Typography variant="h4" sx={{ textAlign: "center", width: "100%" }}>
          {clickedBtn === "add" ? "Add Campaign" : "Update Campaign"}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "16px",
          maxHeight: "65vh",
          overflowY: "auto",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="center">
              <InputLabel sx={{ minWidth: 120 }}>Trunk*</InputLabel>
              <FormControl
                fullWidth
                error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
              >
                <Autocomplete
                  options={trunkOption}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    trunkOption.find((opt) => opt.id.toString() === formik.values.trunk_id) || null
                  }
                  onChange={(_, newValue) => {
                    formik.setFieldValue("trunk_id", newValue ? newValue.id.toString() : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Trunk"
                      error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
                      helperText={formik.touched.trunk_id && formik.errors.trunk_id}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </Grid>
          {/* Campaign Name */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="center">
              <InputLabel sx={{ minWidth: 120 }}>Campaign Name *</InputLabel>
              <OutlinedInput
                name="campaign_name"
                fullWidth
                placeholder="Enter Campaign Name"
                value={formik.values.campaign_name}
                onChange={formik.handleChange}
                error={formik.touched.campaign_name && Boolean(formik.errors.campaign_name)}
              />
            </Stack>
            {formik.errors.campaign_name && (
              <FormHelperText error sx={{ marginLeft: "150px" }}>
                {formik.errors.campaign_name}
              </FormHelperText>
            )}
          </Grid>
          {/* Description */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="center">
              <InputLabel sx={{ minWidth: 120 }}>Description</InputLabel>
              <OutlinedInput
                name="description"
                fullWidth
                placeholder="Enter Description"
                value={formik.values.description}
                onChange={formik.handleChange}
              />
            </Stack>
            {formik.errors.description && (
              <FormHelperText error sx={{ marginLeft: "150px" }}>
                {formik.errors.description}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="center">
              <InputLabel sx={{ minWidth: 120 }}>Caller Id</InputLabel>
              <OutlinedInput
                name="caller_id"
                fullWidth
                placeholder="Enter caller_id"
                value={formik.values.caller_id}
                onChange={formik.handleChange}
              />
            </Stack>
            {formik.errors.caller_id && (
              <FormHelperText error sx={{ marginLeft: "150px" }}>
                {formik.errors.caller_id}
              </FormHelperText>
            )}
          </Grid>
          {/* <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="center">
              <InputLabel sx={{ minWidth: 120 }}>Audio Mode *</InputLabel>
              <RadioGroup
                row
                name="audio_mode"
                value={formik.values.audio_mode}
                onChange={formik.handleChange}
              >
                <FormControlLabel value="upload" control={<Radio />} label="Upload" />
                <FormControlLabel value="create" control={<Radio />} label="create" />
              </RadioGroup>
            </Stack>
          </Grid> */}
          {/* {formik.values.audio_mode === "upload" && ( */}
          <>
            {/* Audio File */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={3} alignItems="center">
                <InputLabel sx={{ minWidth: 120 }}>Audio File 1*</InputLabel>
                <OutlinedInput
                  name="audio_file"
                  type="file"
                  fullWidth
                  inputProps={{ accept: "audio/*" }}
                  onChange={(event) =>
                    formik.setFieldValue("audio_file", event.currentTarget.files[0])
                  }
                  error={formik.touched.audio_file && Boolean(formik.errors.audio_file)}
                />
              </Stack>
              {formik.errors.audio_file && (
                <FormHelperText error sx={{ marginLeft: "150px" }}>
                  {formik.errors.audio_file}
                </FormHelperText>
              )}
            </Grid>

            {/* <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <InputLabel sx={{ minWidth: 138 }}>Audio File 2</InputLabel>
                  <OutlinedInput
                    name="audio_file2"
                    type="file"
                    fullWidth
                    displayEmpty
                    inputProps={{ accept: "audio/*" }}
                    onChange={(event) =>
                      formik.setFieldValue("audio_file2", event.currentTarget.files[0])
                    }
                    error={formik.touched.audio_file2 && Boolean(formik.errors.audio_file2)}
                  />
                </Stack>
                {formik.errors.audio_file2 && (
                  <FormHelperText error sx={{ marginLeft: "150px" }}>
                    {formik.errors.audio_file2}
                  </FormHelperText>
                )}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 100 }}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={[5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60]} // dropdown options
                      value={formik.values.hold_time || null} // null rakho jab empty ho
                      onChange={(event, newValue) => {
                        formik.setFieldValue("hold_time", newValue); // Formik ke value update karna
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Hold Time"
                          fullWidth
                          size="medium"
                          sx={{
                            "& .MuiInputBase-root": {
                              height: 42,
                              display: "flex",
                              alignItems: "center", // ✅ text vertical center me aayega
                              padding: "0 10px", // ✅ left-right padding thoda better dikhane ke liye
                            },
                            "& .MuiInputBase-input": {
                              padding: 0, // ✅ input ke andar ka extra padding remove
                            },
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Stack>
                {formik.errors.hold_time && (
                  <FormHelperText error sx={{ marginLeft: "150px" }}>
                    {formik.errors.hold_time}
                  </FormHelperText>
                )}
              </Stack>
            </Grid> */}

            {/* <Grid item xs={12}>
              <Stack direction="row" spacing={3} alignItems="center">
                <InputLabel sx={{ minWidth: 120 }}>Audio File 3</InputLabel>
                <OutlinedInput
                  name="audio_file3"
                  type="file"
                  fullWidth
                  inputProps={{ accept: "audio/*" }}
                  onChange={(event) =>
                    formik.setFieldValue("audio_file3", event.currentTarget.files[0])
                  }
                />
              </Stack>
              {formik.errors.audio_file3 && (
                <FormHelperText error sx={{ marginLeft: "150px" }}>
                  {formik.errors.audio_file3}
                </FormHelperText>
              )}
            </Grid> */}
          </>

          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableBody>
                  {formik.values.routes.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ width: "35%", py: 1.5, px: 2 }}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={formik.touched.input_value && Boolean(formik.errors.input_value)}
                        >
                          <InputLabel htmlFor={`routes[${index}].input_value`}>
                            Input-Value
                          </InputLabel>
                          <OutlinedInput
                            id={`routes[${index}].input_value`}
                            name={`routes[${index}].input_value`}
                            label="Input-Value"
                            value={formik.values.routes[index]?.input_value || ""}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.routes?.[index]?.input_value &&
                              Boolean(formik.errors.routes?.[index]?.input_value)
                            }
                          />

                          {formik.touched.routes?.[index]?.input_value &&
                            formik.errors.routes?.[index]?.input_value && (
                              <FormHelperText error>
                                {formik.errors.routes[index].input_value}
                              </FormHelperText>
                            )}
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ width: "45%", py: 1.5 }}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={formik.touched.destination && Boolean(formik.errors.destination)}
                        >
                          <InputLabel htmlFor={`routes[${index}].destination`}>
                            Destination
                          </InputLabel>
                          <OutlinedInput
                            id={`routes[${index}].destination`}
                            name={`routes[${index}].destination`}
                            label="Destination"
                            value={formik.values.routes[index]?.destination || ""}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.routes?.[index]?.destination &&
                              Boolean(formik.errors.routes?.[index]?.destination)
                            }
                          />
                          {formik.touched.routes?.[index]?.destination &&
                            formik.errors.routes?.[index]?.destination && (
                              <FormHelperText error>
                                {formik.errors.routes[index].destination}
                              </FormHelperText>
                            )}
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ width: "20%", py: 1.5 }}>
                        <Box display="flex" justifyContent="flex-end">
                          {index === 0 ? (
                            <Button
                              variant="contained"
                              onClick={handleAddRoute}
                              sx={{
                                minWidth: 36,
                                height: 36,
                                borderRadius: "12px",
                                color: "#ffffff", // white text/icon
                              }}
                            >
                              +
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              onClick={() => handleRemoveRoute(index)}
                              sx={{
                                backgroundColor: "#e84653ff",
                                minWidth: 36,
                                height: 36,
                                borderRadius: "12px",
                                color: "#ffffff", // white text/icon
                                "&:hover": {
                                  backgroundColor: "#e65c6aff",
                                },
                              }}
                            >
                              -
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>

      {/* Submit Button */}
      <Grid item xs={12} p={2}>
        <Button
          variant="contained"
          type="submit"
          fullWidth
          disabled={formik.isSubmitting} // 👈 Ye line add karo
          sx={{
            backgroundColor: "#1A73E8",
            color: "#FFFFFF",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#1669C1",
            },
          }}
        >
          {formik.isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Grid>
    </form>
  );
}

AddCampaignForm.propTypes = {
  onSuccess: PropTypes.func,
  initialData: PropTypes.object,
  clickedBtn: PropTypes.string,
  setSubmitting: PropTypes.func,
};

export default AddCampaignForm;
